import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { deleteHospital, deleteOperatingRoom } from '../services/api.ts';
import type { Hospital, HospitalDeleteSummary, OperatingRoom } from '../services/api.ts';
import { useCachedRows, hospitalsQuery as hospitalsQueryOptions, operatingRoomsQuery as operatingRoomsQueryOptions } from '../data/queries.ts';
import AdminOnly from './AdminOnly.tsx';
import ReadOnlyBanner from './ReadOnlyBanner.tsx';
import Toast from './Toast.tsx';
import HospitalsTable from './HospitalsTable.tsx';
import HospitalFormDialog from './HospitalFormDialog.tsx';
import RoomFormDialog from './RoomFormDialog.tsx';
import { searchHospitals } from './hospitalSearch.ts';

// Hospitals & ORs page: one table of hospitals, each opening onto its rooms, narrowed by one search.
// Creating or editing a hospital or room opens a dialog, matching the Providers page. (Replaces the
// former HospitalForm, whose create form sat above the table and whose only edit was an inline rename.)

type DialogState =
  | { kind: 'hospital'; hospital: Hospital | null }
  | { kind: 'room'; room: OperatingRoom | null; hospitalId?: string }
  | null;

type ToastState = { type: 'success' | 'error' | 'warning' | 'info'; text: string } | null;

/** "+ Create" with a small menu: a new hospital, or new rooms at an existing one. */
const CreateMenu: React.FC<{ onCreateHospital: () => void; onCreateRoom: () => void; canCreateRoom: boolean }> = ({
  onCreateHospital, onCreateRoom, canCreateRoom,
}) => {
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    const onPointer = (e: MouseEvent) => { if (!ref.current?.contains(e.target as Node)) setOpen(false); };
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', onPointer);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onPointer);
      document.removeEventListener('keydown', onKey);
    };
  }, [open]);

  const choose = (action: () => void) => () => { setOpen(false); action(); };

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      <button type="button" className="btn btn-primary" aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen(o => !o)}>
        + Create ▾
      </button>
      {open && (
        <div role="menu" className="create-menu">
          <button type="button" role="menuitem" onClick={choose(onCreateHospital)}>Hospital</button>
          <button
            type="button"
            role="menuitem"
            onClick={choose(onCreateRoom)}
            disabled={!canCreateRoom}
            title={canCreateRoom ? undefined : 'Create a hospital first'}
          >
            Room
          </button>
        </div>
      )}
    </div>
  );
};

const HospitalsView: React.FC = () => {
  // Read through the shared cache (data/queries.ts). Mutations in services/api.ts mark it stale, so the
  // table, the Board and every other screen refresh on their own; deletes also drop the row at once.
  const [hospitals, setHospitals, hospitalsQuery] = useCachedRows<Hospital>(hospitalsQueryOptions());
  const [operatingRooms, setOperatingRooms] = useCachedRows<OperatingRoom>(operatingRoomsQueryOptions());

  const [search, setSearch] = useState('');
  const [dialog, setDialog] = useState<DialogState>(null);
  const [toast, setToast] = useState<ToastState>(null);
  const [deletingHospitalId, setDeletingHospitalId] = useState<string | null>(null);
  const [busyRoomId, setBusyRoomId] = useState<string | null>(null);
  // A hospital found only through one of its rooms opens by itself; a click on its toggle overrides that.
  const [expandOverrides, setExpandOverrides] = useState<Record<string, boolean>>({});
  // Stable, so the Toast's auto-dismiss timer isn't restarted by every re-render.
  const dismissToast = useCallback(() => setToast(null), []);

  useEffect(() => { if (hospitalsQuery.error) console.error('Error loading hospitals:', hospitalsQuery.error); }, [hospitalsQuery.error]);
  // A new search starts from its own auto-expansion.
  useEffect(() => { setExpandOverrides({}); }, [search]);

  const results = useMemo(() => searchHospitals(hospitals, operatingRooms, search), [hospitals, operatingRooms, search]);
  const autoExpanded = useMemo(
    () => new Set(results.filter(r => r.matchedByRoom).map(r => r.hospital.hospitalId)),
    [results]
  );
  const isExpanded = (hospitalId: string) => expandOverrides[hospitalId] ?? autoExpanded.has(hospitalId);
  const toggleExpand = (hospitalId: string) =>
    setExpandOverrides(prev => ({ ...prev, [hospitalId]: !(prev[hospitalId] ?? autoExpanded.has(hospitalId)) }));

  const handleDeleteHospital = async (hospital: Hospital) => {
    setDeletingHospitalId(hospital.hospitalId);
    try {
      // First ask the backend exactly what would be removed, so the confirmation is honest.
      const preview = await deleteHospital(hospital.hospitalId, { dryRun: true });
      const w = (preview.wouldDelete || {}) as HospitalDeleteSummary;
      const lines = [
        `Delete “${hospital.name}” and everything under it?`,
        '',
        'This will permanently remove:',
        `  • the hospital itself`,
        `  • ${w.operatingRooms ?? 0} operating room(s)`,
        `  • ${w.schedules ?? 0} scheduled case(s)`,
        `  • ${w.allocations ?? 0} allocation(s)`,
        `  • ${w.distanceRows ?? 0} hospital-distance link(s)`,
        '',
        `${w.employeesHomeBaseCleared ?? 0} staff member(s) list this as their home base — they will be KEPT, with their home base cleared.`,
        '',
        'This cannot be undone. Continue?',
      ];
      if (!window.confirm(lines.join('\n'))) return;

      const res = await deleteHospital(hospital.hospitalId);
      const d = (res.deleted || w) as HospitalDeleteSummary;
      // Drop it from view now; the cascade (rooms, cases, allocations, home bases) refetches behind it.
      setHospitals(prev => prev.filter(h => h.hospitalId !== hospital.hospitalId));
      setOperatingRooms(prev => prev.filter(r => r.hospitalId !== hospital.hospitalId));
      setToast({
        type: 'success',
        text: `Deleted “${hospital.name}” — removed ${d.operatingRooms ?? 0} room(s), ${d.schedules ?? 0} case(s), ${d.allocations ?? 0} allocation(s); cleared home base for ${d.employeesHomeBaseCleared ?? 0} staff.`,
      });
    } catch (error: any) {
      setToast({ type: 'error', text: error.response?.data?.error || error.response?.data?.message || 'Failed to delete hospital' });
    } finally {
      setDeletingHospitalId(null);
    }
  };

  const handleDeleteRoom = async (room: OperatingRoom) => {
    if (!window.confirm(`Delete “${room.name}”? This can’t be undone.`)) return;
    setBusyRoomId(room.operatingRoomId);
    try {
      await deleteOperatingRoom(room.operatingRoomId);
      setOperatingRooms(prev => prev.filter(r => r.operatingRoomId !== room.operatingRoomId));
      setToast({ type: 'success', text: `Deleted “${room.name}”.` });
    } catch (error: any) {
      setToast({ type: 'error', text: error.response?.data?.error || 'Failed to delete room' });
    } finally {
      setBusyRoomId(null);
    }
  };

  const onSaved = (text: string, type: 'success' | 'warning' = 'success') => setToast({ type, text });

  return (
    <>
      {toast && <Toast message={toast.text} type={toast.type} onClose={dismissToast} />}

      <div className="card">
        <h1>Hospitals &amp; ORs</h1>
        <ReadOnlyBanner />

        <section className="hospitals-table" aria-labelledby="hospitals-heading">
          <div style={{ display: 'flex', alignItems: 'center', gap: '12px', flexWrap: 'wrap', marginBottom: '16px' }}>
            <h2 id="hospitals-heading" style={{ margin: 0, fontSize: '20px' }}>
              Hospitals &amp; rooms
              {!hospitalsQuery.isPending && (
                <span style={{ marginLeft: '8px', fontSize: '0.65em', fontWeight: 400, color: 'var(--text-muted)' }}>
                  {hospitals.length} hospital{hospitals.length === 1 ? '' : 's'} · {operatingRooms.length} room{operatingRooms.length === 1 ? '' : 's'}
                </span>
              )}
            </h2>
            <div style={{ flex: 1 }} />
            <input
              type="search"
              className="form-control"
              aria-label="Search hospitals and rooms"
              placeholder="🔍 Search hospitals and rooms…"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ maxWidth: '280px' }}
            />
            <AdminOnly>
              <CreateMenu
                onCreateHospital={() => setDialog({ kind: 'hospital', hospital: null })}
                onCreateRoom={() => setDialog({ kind: 'room', room: null })}
                canCreateRoom={hospitals.length > 0}
              />
            </AdminOnly>
          </div>

          {hospitalsQuery.isError && !hospitalsQuery.data && (
            <div className="alert alert-error">Failed to load hospitals.</div>
          )}

          <HospitalsTable
            results={results}
            totalCount={hospitals.length}
            loading={hospitalsQuery.isPending}
            searchQuery={search}
            isExpanded={isExpanded}
            onToggleExpand={toggleExpand}
            onEditHospital={hospital => setDialog({ kind: 'hospital', hospital })}
            onDeleteHospital={handleDeleteHospital}
            onAddRoom={hospital => setDialog({ kind: 'room', room: null, hospitalId: hospital.hospitalId })}
            onEditRoom={room => setDialog({ kind: 'room', room })}
            onDeleteRoom={handleDeleteRoom}
            deletingHospitalId={deletingHospitalId}
            busyRoomId={busyRoomId}
          />
        </section>
      </div>

      <HospitalFormDialog
        open={dialog?.kind === 'hospital'}
        hospital={dialog?.kind === 'hospital' ? dialog.hospital : null}
        onClose={() => setDialog(null)}
        onSaved={onSaved}
      />
      <RoomFormDialog
        open={dialog?.kind === 'room'}
        room={dialog?.kind === 'room' ? dialog.room : null}
        defaultHospitalId={dialog?.kind === 'room' ? dialog.hospitalId : undefined}
        hospitals={hospitals}
        allRooms={operatingRooms}
        onClose={() => setDialog(null)}
        onSaved={message => onSaved(message)}
      />
    </>
  );
};

export default HospitalsView;
